import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import LocationIcon from '@mui/icons-material/esm/LocationOn';
import AddIcon from '@mui/icons-material/esm/Add';
import CloseIcon from '@mui/icons-material/esm/Close';
import type { Task } from '../../services/api/tasksService';
import workLocationsService, { type WorkLocation as WorkLocationOption } from '../../services/api/workLocationsService';

/**
 * Derive a display string for a task's location, preferring (in order):
 * 1. The managed WorkLocation (via FK ref)
 * 2. The legacy freeform tasks.work_location string
 * 3. The Unit name/code of the task's component
 * 4. The component name/code itself
 * Falls back to "Location not set" if nothing is available.
 */
export function getTaskLocation(task: Task): string {
  if (task.workLocationRef?.name) return task.workLocationRef.name;
  if (task.workLocation && task.workLocation.trim()) return task.workLocation.trim();
  const unit = task.component?.unit;
  if (unit?.name) return unit.name;
  if (unit?.code) return unit.code;
  if (task.component?.name) return task.component.name;
  if (task.component?.code) return task.component.code;
  return 'Location not set';
}

/**
 * Clickable location chip that opens a menu of WorkLocations + "Create new".
 */
export function LocationPicker({
  task,
  locations,
  onPicked,
  onCreateRequest,
  onLocationDeleted,
}: {
  task: Task;
  locations: WorkLocationOption[];
  onPicked: (locationId: string | null) => void;
  onCreateRequest: () => void;
  /** Called after a location is successfully deleted via right-click, so the
   *  parent can refresh its locations list. */
  onLocationDeleted?: () => void;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const label = getTaskLocation(task);
  const currentId = task.workLocationId ?? null;

  const handleContextDelete = async (e: React.MouseEvent, loc: WorkLocationOption) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Delete location "${loc.name}"? Tasks using it lose the reference.`)) return;
    try {
      await workLocationsService.remove(loc.id);
      // If the currently-assigned location was deleted, clear it from this task.
      if (loc.id === currentId) onPicked(null);
      onLocationDeleted?.();
      setAnchorEl(null);
    } catch (err: any) {
      window.alert(err?.response?.data?.error || 'Failed to delete location');
    }
  };

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
        sx={{
          color: 'text.secondary',
          minWidth: 0,
          cursor: 'pointer',
          borderRadius: 1,
          px: 0.5,
          py: 0.25,
          ml: -0.5,
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        <LocationIcon fontSize="inherit" sx={{ fontSize: 18, flexShrink: 0 }} />
        <Typography variant="body2" noWrap title={label}>{label}</Typography>
      </Stack>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        onClick={(e) => e.stopPropagation()}
        MenuListProps={{ dense: true }}
      >
        <MenuItem
          selected={currentId === null}
          onClick={() => {
            onPicked(null);
            setAnchorEl(null);
          }}
        >
          <em>None</em>
        </MenuItem>
        <Divider />
        {locations.length === 0 ? (
          <MenuItem disabled>No locations yet</MenuItem>
        ) : (
          locations.map((loc) => (
            <MenuItem
              key={loc.id}
              selected={loc.id === currentId}
              onClick={() => {
                onPicked(loc.id);
                setAnchorEl(null);
              }}
              onContextMenu={(e) => handleContextDelete(e, loc)}
              sx={{ pr: 1 }}
            >
              <Box sx={{ flex: 1 }}>{loc.name}</Box>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  edge="end"
                  onClick={(e) => handleContextDelete(e, loc)}
                  sx={{ p: 0.25, ml: 1 }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </MenuItem>
          ))
        )}
        {locations.length > 0 && (
          <Box sx={{ px: 2, pb: 0.5 }}>
            <Typography variant="caption" color="text.disabled">
              Right-click or click the × to delete a location.
            </Typography>
          </Box>
        )}
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onCreateRequest();
          }}
        >
          + Create new location…
        </MenuItem>
      </Menu>
    </>
  );
}

/**
 * Bordered "Location" panel that wraps a LocationPicker with a label.
 */
export function LocationBox({
  task,
  locations,
  onPicked,
  onCreateRequest,
  onLocationDeleted,
}: {
  task: Task;
  locations: WorkLocationOption[];
  onPicked: (locationId: string | null) => void;
  onCreateRequest: () => void;
  onLocationDeleted?: () => void;
}) {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 0.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
      >
        Location
      </Typography>
      <LocationPicker
        task={task}
        locations={locations}
        onPicked={onPicked}
        onCreateRequest={onCreateRequest}
        onLocationDeleted={onLocationDeleted}
      />
    </Box>
  );
}

/**
 * Bordered "Specifications" panel — list of free-form strings with add/delete.
 * Calls onChange(taskId, newArray) on every mutation; parent persists via
 * tasksService.updateTask.
 */
export interface SpecItem {
  text: string;
  done: boolean;
}

export function SpecificationsBox({
  task,
  onChange,
}: {
  task: Task;
  onChange: (taskId: string, specifications: SpecItem[]) => void;
}) {
  const [draft, setDraft] = useState('');
  const specs: SpecItem[] = task.specifications ?? [];

  const handleAdd = () => {
    const text = draft.trim();
    if (!text) return;
    onChange(task.id, [...specs, { text, done: false }]);
    setDraft('');
  };

  const handleToggle = (idx: number) => {
    onChange(
      task.id,
      specs.map((s, i) => (i === idx ? { ...s, done: !s.done } : s))
    );
  };

  const handleRemove = (idx: number) => {
    onChange(task.id, specs.filter((_, i) => i !== idx));
  };

  const doneCount = specs.filter((s) => s.done).length;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
          Specifications
        </Typography>
        {specs.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            {doneCount}/{specs.length} done
          </Typography>
        )}
      </Stack>
      {specs.length === 0 ? (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1 }}>
          No specifications yet.
        </Typography>
      ) : (
        <Stack spacing={0} sx={{ mb: 1 }}>
          {specs.map((spec, idx) => (
            <Stack key={idx} direction="row" spacing={0.5} alignItems="center">
              <Checkbox
                size="small"
                checked={spec.done}
                onChange={() => handleToggle(idx)}
                sx={{ p: 0.5 }}
              />
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  wordBreak: 'break-word',
                  textDecoration: spec.done ? 'line-through' : 'none',
                  color: spec.done ? 'text.disabled' : 'text.primary',
                }}
              >
                {spec.text}
              </Typography>
              <Tooltip title="Remove">
                <IconButton size="small" onClick={() => handleRemove(idx)} sx={{ p: 0.25 }}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          ))}
        </Stack>
      )}
      <Stack direction="row" spacing={0.5} alignItems="center">
        <TextField
          size="small"
          fullWidth
          placeholder="Add specification…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
        />
        <Tooltip title="Add">
          <span>
            <IconButton size="small" onClick={handleAdd} disabled={!draft.trim()}>
              <AddIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Box>
  );
}

/**
 * Bordered "Conditions" panel — free-form labels separate from the locked
 * lifecycle_status enum. Same shape as SpecificationsBox but for custom
 * status-like tags (e.g. "client hold", "weather delayed", "engineer
 * sign-off needed"). Edits persist via the parent's onChange (which
 * should PATCH tasks.conditions).
 */
export function ConditionsBox({
  task,
  onChange,
}: {
  task: Task;
  onChange: (taskId: string, conditions: string[]) => void;
}) {
  const [draft, setDraft] = useState('');
  const items = task.conditions ?? [];

  const handleAdd = () => {
    const text = draft.trim();
    if (!text) return;
    onChange(task.id, [...items, text]);
    setDraft('');
  };

  const handleRemove = (idx: number) => {
    onChange(task.id, items.filter((_, i) => i !== idx));
  };

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 0.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
      >
        Conditions
      </Typography>
      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1 }}>
        Custom labels alongside the engine status (e.g. "client hold", "engineer sign-off needed").
      </Typography>
      {items.length === 0 ? (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1 }}>
          No conditions yet.
        </Typography>
      ) : (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          {items.map((cond, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.25,
                px: 0.75,
                py: 0.25,
                bgcolor: 'action.selected',
                borderRadius: 99,
                fontSize: 12,
              }}
            >
              <span>{cond}</span>
              <Tooltip title="Remove">
                <IconButton size="small" onClick={() => handleRemove(idx)} sx={{ p: 0 }}>
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Stack>
      )}
      <Stack direction="row" spacing={0.5} alignItems="center">
        <TextField
          size="small"
          fullWidth
          placeholder="Add condition…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
        />
        <Tooltip title="Add">
          <span>
            <IconButton size="small" onClick={handleAdd} disabled={!draft.trim()}>
              <AddIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Box>
  );
}

/**
 * Modal for creating a new WorkLocation. Submits to onSubmit(name) and
 * closes via onClose. Local input state lives here; parent only sees the
 * final submitted name.
 */
export function CreateLocationDialog({
  open,
  initialError,
  onSubmit,
  onClose,
}: {
  open: boolean;
  initialError?: string | null;
  onSubmit: (name: string) => Promise<void> | void;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(initialError ?? null);

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Name is required');
      return;
    }
    try {
      await onSubmit(trimmed);
      setName('');
      setError(null);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to create location');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Create work location</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          label="Location name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
          }}
          error={Boolean(error)}
          helperText={error || 'e.g. "Lot 47 — Master Bath", "Block B Foundation"'}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { setName(''); setError(null); onClose(); }}>Cancel</Button>
        <Button variant="contained" onClick={submit}>Create &amp; assign</Button>
      </DialogActions>
    </Dialog>
  );
}
